/**
 * BPContentsCard — the « Contenus cités » block of a Brand Perception axis's Contenu
 * sub-tab (docs/features/brand-perception.md §9.13).
 *
 * Owns its own fetch (`…/dashboard/contents`, the same debounced idiom as the parent)
 * because it is a different endpoint with its own grain (day ≤ 31 d, ISO week 32–90 d,
 * month > 90 d) — never `BPDashboardGrain`.
 *
 * What the response's shape imposes here:
 *  1. `line` carries MEASURED buckets only. An absent bucket is a GAP in the line, never
 *     a point at 0; `partial` edge buckets get a hollow marker so a short first/last
 *     week does not read as a citation drop.
 *  2. Past 90 days the line and table come from the WRITTEN monthly tier (`BPTOPM`,
 *     Lot 9): an empty month window is not measured, never « 0 ».
 *  3. Dots are contents PUBLISHED in a bucket (`publishedAt`). Undated contents are not
 *     placed anywhere: they are counted in « N contenus sans date ».
 *  4. Linked products are resolved through `POST …/targets/by-id` for the VISIBLE table
 *     page only — one call per page change, never the whole table. A missing label shows
 *     the url; a miss is « Cible non résolue », never dropped, never a guessed name.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  CARD,
  ChartCard,
  EmptyNote,
  NoticePanel,
  VizLegend,
  VizTable,
  VIZ,
  fmtInt,
  niceTicks,
  useMeasuredWidth,
  useVizTooltip,
} from '../viz';
import { BP_MAX_SPAN_DAYS } from './bpRange';
import { api } from '../../services/api';
import { targetsService } from '../../services/targets';
import { sourceHref } from '../../utils/sourceUrl';
import type {
  BPContentsGrain,
  BPContentsResponse,
  BPContentsTableRow,
  BPTargetsAxis,
  ContenuContentType,
} from '@traaker/shared';

/** Table rows per page — and therefore the most rows one by-id call resolves for. */
const PAGE_SIZE = 10;
/** Linked-product chips shown inline before « +N ». */
const CHIPS_INLINE = 4;
const MS_PER_DAY = 86_400_000;

type TypeKey = ContenuContentType | 'unknown';

// ---------------------------------------------------------------------------
// Bucket helpers — UTC, same keys as the API (day / Monday / YYYY-MM)
// ---------------------------------------------------------------------------

const dayMs = (d: string) => Date.parse(`${d}T00:00:00Z`);
const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

function mondayOf(day: string): string {
  const ms = dayMs(day);
  const dow = (new Date(ms).getUTCDay() + 6) % 7; // Monday = 0
  return isoDay(ms - dow * MS_PER_DAY);
}

function nextMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

/** Every bucket key the window covers, so an unmeasured bucket has a slot to be a gap in. */
function bucketsOf(from: string, to: string, grain: BPContentsGrain): string[] {
  const out: string[] = [];
  if (grain === 'day') {
    for (let ms = dayMs(from); ms <= dayMs(to); ms += MS_PER_DAY) out.push(isoDay(ms));
  } else if (grain === 'week') {
    for (let ms = dayMs(mondayOf(from)); ms <= dayMs(to); ms += 7 * MS_PER_DAY) out.push(isoDay(ms));
  } else {
    for (let ym = from.slice(0, 7); ym <= to.slice(0, 7); ym = nextMonth(ym)) out.push(ym);
  }
  return out;
}

/** Days of `bucket` that fall inside the window — the denominator of « jours mesurés ». */
function daysInWindow(bucket: string, grain: BPContentsGrain, from: string, to: string): number {
  let start: number;
  let end: number;
  if (grain === 'day') return 1;
  if (grain === 'week') {
    start = dayMs(bucket);
    end = start + 6 * MS_PER_DAY;
  } else {
    start = dayMs(`${bucket}-01`);
    end = dayMs(`${nextMonth(bucket)}-01`) - MS_PER_DAY;
  }
  const s = Math.max(start, dayMs(from));
  const e = Math.min(end, dayMs(to));
  return e < s ? 0 : Math.round((e - s) / MS_PER_DAY) + 1;
}

/** The cible's url shown AS a url (scheme + trailing slash dropped) — never a made-up name. */
function urlLabel(u?: string): string {
  if (!u) return '';
  const compact = u.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  return compact.length > 64 ? `${compact.slice(0, 63)}…` : compact;
}

const codeSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontStyle: 'normal',
  fontSize: 12,
  color: VIZ.ink2,
  bgcolor: '#F3F3F5',
  border: '1px solid #E8E8EC',
  borderRadius: '6px',
  px: 0.875,
  py: '2px',
} as const;

type Linked = { label?: string; url?: string; notFound?: true; failed?: true };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface BPContentsCardProps {
  brandId: string;
  axis: BPTargetsAxis;
  /** The window AFTER `resolveBpRange` — inclusive days. */
  from: string;
  to: string;
  spanClamped: boolean;
  /** The Contenu tab's tag filter is active: this block does not take it, so it says so. */
  tagFilterActive: boolean;
}

export default function BPContentsCard({
  brandId,
  axis,
  from,
  to,
  spanClamped,
  tagFilterActive,
}: BPContentsCardProps) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<BPContentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [page, setPage] = useState(0);
  const [linked, setLinked] = useState<Map<string, Linked>>(new Map());

  useEffect(() => {
    if (!brandId) return;
    const params = new URLSearchParams({ axis, from, to });
    setLoading(true);
    // A slow earlier response must not overwrite the current window's.
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .get<BPContentsResponse>(`/brands/${brandId}/brand-perception/dashboard/contents?${params}`)
        .then((resp) => {
          if (cancelled) return;
          setData(resp ?? null);
          setLoadError('');
          setPage(0);
        })
        .catch((err: any) => {
          if (cancelled) return;
          setData(null);
          setLoadError(err?.message || t('bpDash.loadError'));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, axis, from, to]);

  const table = data?.table ?? [];
  const pageCount = Math.max(1, Math.ceil(table.length / PAGE_SIZE));
  const visible = table.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // One by-id call per page change, for the ids on screen not already resolved.
  const linkedRef = useRef(linked);
  linkedRef.current = linked;
  useEffect(() => {
    const ids = Array.from(new Set(visible.flatMap((r) => r.relatedTargetIds ?? []))).filter(
      (id) => !linkedRef.current.has(id),
    );
    if (!brandId || ids.length === 0) return;
    let cancelled = false;
    targetsService
      .getByIds(brandId, ids.map((targetId) => ({ targetId })))
      .then((resp) => {
        if (cancelled) return;
        setLinked((prev) => {
          const next = new Map(prev);
          for (const it of resp.items) next.set(it.targetId, { label: it.label, url: it.url });
          for (const id of resp.notFound) next.set(id, { notFound: true });
          return next;
        });
      })
      .catch(() => {
        if (cancelled) return;
        setLinked((prev) => {
          const next = new Map(prev);
          for (const id of ids) next.set(id, { failed: true });
          return next;
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, data, page]);

  // ---- formatting -----------------------------------------------------------

  const grain: BPContentsGrain = data?.grain ?? 'day';
  const fmtBucket = (key: string, long = false) => {
    if (key.length === 7) {
      return new Date(`${key}-01T00:00:00Z`).toLocaleDateString(i18n.language, {
        month: long ? 'long' : 'short',
        year: long ? 'numeric' : '2-digit',
        timeZone: 'UTC',
      });
    }
    const d = new Date(`${key}T00:00:00Z`).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
      ...(long ? { year: 'numeric' } : {}),
      timeZone: 'UTC',
    });
    return long && grain === 'week' ? t('bpContents.weekOf', { date: d }) : d;
  };
  const fmtDate = (iso?: string) =>
    iso
      ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString(i18n.language, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        })
      : '';
  const typeLabel = (k: TypeKey) =>
    k === 'unknown' ? t('bpContents.typeUnknown') : t(`cibles.contentType.${k}`);

  // ---- caveats --------------------------------------------------------------

  const caveatLines = useMemo(() => {
    if (!data) return [];
    const out: string[] = [t('bpContents.lineScope')];
    if (spanClamped) out.push(t('bpDash.caveat.spanClamped', { days: fmtInt(BP_MAX_SPAN_DAYS) }));
    if (data.clamped) out.push(t('bpDash.caveat.clamped'));
    if (data.availableFrom && data.from < data.availableFrom) {
      out.push(t('bpTargets.availableFrom', { date: fmtDate(data.availableFrom) }));
    }
    for (const c of data.caveats) {
      // Stated with its date above / by the gaps in the line.
      if (c === 'window_precedes_target_data') continue;
      out.push(t(`bpDash.caveat.${c}`));
    }
    if (data.line.some((p) => p.topTruncated)) out.push(t('bpContents.topTruncated'));
    if (data.line.some((p) => p.partial) || data.dots.buckets.some((b) => b.partial)) {
      out.push(t('bpContents.partialBuckets'));
    }
    if (data.incomplete) out.push(t('bpContents.incomplete'));
    if (data.unresolvedCount > 0) out.push(t('bpContents.unresolvedRows', { count: data.unresolvedCount }));
    if (data.dots.truncated) out.push(t('bpContents.dotsTruncated'));
    if (tagFilterActive) out.push(t('bpContents.tagFilterIgnored'));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, spanClamped, tagFilterActive, i18n.language]);

  // ---- chart model ------------------------------------------------------------

  const model = useMemo(() => {
    if (!data) return null;
    const keys = bucketsOf(data.from, data.to, data.grain);
    // Defensive union: a bucket the API names that this helper did not produce still
    // gets its slot rather than being silently dropped.
    const known = new Set(keys);
    for (const k of [...data.line.map((p) => p.bucket), ...data.dots.buckets.map((b) => b.bucket)]) {
      if (!known.has(k)) {
        keys.push(k);
        known.add(k);
      }
    }
    keys.sort();
    const lineBy = new Map(data.line.map((p) => [p.bucket, p]));
    const dotBy = new Map(data.dots.buckets.map((b) => [b.bucket, b]));

    // Colour: `VIZ.series` is fixed-order and never cycled, so only the 4 most-published
    // content types get a hue; the rest share the neutral « Autres types » swatch.
    const typeTotals = new Map<TypeKey, number>();
    for (const b of data.dots.buckets) {
      for (const [k, n] of Object.entries(b.byContentType)) {
        typeTotals.set(k as TypeKey, (typeTotals.get(k as TypeKey) ?? 0) + (n ?? 0));
      }
    }
    for (const it of data.dots.items ?? []) {
      const k: TypeKey = it.contentType ?? 'unknown';
      if (!data.dots.buckets.length) typeTotals.set(k, (typeTotals.get(k) ?? 0) + 1);
    }
    const ranked = [...typeTotals.entries()]
      .filter(([k]) => k !== 'unknown')
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => k);
    const colored = ranked.slice(0, VIZ.series.length);
    const colorOf = (k: TypeKey) => {
      const i = colored.indexOf(k);
      return i >= 0 ? VIZ.series[i] : VIZ.nodata;
    };
    const legend = colored.map((k) => ({ label: typeLabel(k), color: colorOf(k) }));
    if (ranked.length > colored.length || typeTotals.has('unknown')) {
      legend.push({ label: t('bpContents.typeOther'), color: VIZ.nodata });
    }

    // Day grain: one dot per content (from `items`); otherwise stacked counts per type.
    const itemsBy = new Map<string, { color: string; title?: string }[]>();
    if (data.grain === 'day' && data.dots.items) {
      for (const it of data.dots.items) {
        const k = it.publishedAt.slice(0, 10);
        const arr = itemsBy.get(k) ?? [];
        arr.push({ color: colorOf(it.contentType ?? 'unknown'), title: it.title });
        itemsBy.set(k, arr);
      }
    }
    return { keys, lineBy, dotBy, itemsBy, colorOf, legend };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, i18n.language]);

  // ---- render -----------------------------------------------------------------

  const title = t('bpContents.title');
  if (loading) return <Skeleton variant="rounded" height={420} />;
  if (loadError || !data || !model) {
    return (
      <ChartCard title={title} dot={VIZ.violet}>
        <EmptyNote>{loadError || t('bpDash.noData')}</EmptyNote>
      </ChartCard>
    );
  }

  // `measuredDays` is absent at month grain unless the written row states it — never a 0.
  const nMeasured = (p: { measuredDays?: number }, key: string) =>
    p.measuredDays != null
      ? `${fmtInt(p.measuredDays)} / ${fmtInt(daysInWindow(key, data.grain, data.from, data.to))}`
      : '—';

  const chartTable = (
    <VizTable
      columns={[
        t('bpContents.colBucket'),
        t('bpContents.colContentsCited'),
        t('bpTargets.colCitations'),
        t('bpContents.colMeasuredDays'),
        t('bpContents.colPublished'),
      ]}
      align={['left', 'right', 'right', 'right', 'right']}
      rows={model.keys.map((k) => {
        const p = model.lineBy.get(k);
        const na = <Box component="span" sx={{ color: VIZ.nodata }}>{t('bpDash.notMeasuredShort')}</Box>;
        return [
          `${fmtBucket(k, true)}${p?.partial || model.dotBy.get(k)?.partial ? ` · ${t('bpContents.partialShort')}` : ''}`,
          p ? `${p.topTruncated ? '≥ ' : ''}${fmtInt(p.contentsCited)}` : na,
          p ? fmtInt(p.citations) : na,
          p ? nMeasured(p, k) : na,
          fmtInt(model.dotBy.get(k)?.total ?? model.itemsBy.get(k)?.length ?? 0),
        ];
      })}
    />
  );

  const contentCell = (r: BPContentsTableRow) => {
    if (r.unresolved || (!r.label && !r.url)) {
      // An unresolved row may still carry the label recorded at citation time.
      return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: VIZ.muted, fontStyle: 'italic', flexWrap: 'wrap' }}>
          {r.label && <span>{r.label} ·</span>}
          {t('bpTargets.unnamedTargetBare')}
          <Box component="span" sx={codeSx}>{r.targetId.slice(0, 6)}</Box>
        </Box>
      );
    }
    const text = r.label ? (
      r.label
    ) : (
      <Box component="span" sx={{ color: VIZ.muted, fontStyle: 'italic' }}>{urlLabel(r.url)}</Box>
    );
    const href = sourceHref(r.url);
    if (!href) return text;
    return (
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noreferrer"
        title={r.url}
        sx={{ color: 'inherit', textDecoration: 'underline', textDecorationColor: VIZ.axis }}
      >
        {text}
      </Box>
    );
  };

  const chip = (id: string) => {
    const l = linked.get(id);
    const base = {
      display: 'inline-flex',
      alignItems: 'center',
      maxWidth: 200,
      height: 22,
      px: 1,
      borderRadius: '99px',
      fontSize: 12,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    } as const;
    if (!l) return <Skeleton key={id} variant="rounded" width={64} height={22} sx={{ borderRadius: '99px' }} />;
    if (l.notFound || l.failed) {
      return (
        <Tooltip key={id} title={l.failed ? t('bpContents.linkLookupFailed') : id}>
          <Box component="span" sx={{ ...base, bgcolor: '#F3F3F5', color: VIZ.muted, fontStyle: 'italic' }}>
            {l.failed ? id.slice(0, 6) : t('bpTargets.unnamedTargetBare')}
          </Box>
        </Tooltip>
      );
    }
    const text = l.label || urlLabel(l.url) || id.slice(0, 6);
    return (
      <Tooltip key={id} title={l.url ?? ''}>
        <Box
          component="span"
          sx={{ ...base, bgcolor: VIZ.lavender, color: '#5A2FC4', fontWeight: 500, fontStyle: l.label ? undefined : 'italic' }}
        >
          {text}
        </Box>
      </Tooltip>
    );
  };

  const linkedCell = (r: BPContentsTableRow) => {
    const ids = r.relatedTargetIds ?? [];
    if (!ids.length) return <Box component="span" sx={{ color: VIZ.nodata }}>—</Box>;
    const rest = ids.length - CHIPS_INLINE;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {ids.slice(0, CHIPS_INLINE).map(chip)}
        {rest > 0 && (
          <Box component="span" sx={{ fontSize: 12, color: VIZ.muted, alignSelf: 'center' }}>
            {t('bpContents.moreLinked', { n: fmtInt(rest) })}
          </Box>
        )}
      </Box>
    );
  };

  const na = (s: string) => <Box component="span" sx={{ color: VIZ.nodata }}>{s}</Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${CARD.gap * 8}px` }}>
      <ChartCard
        title={title}
        dot={VIZ.violet}
        hint={t('bpContents.hint')}
        chartLabel={t('bpDash.viewChart')}
        tableLabel={t('bpDash.viewTable')}
        table={chartTable}
      >
        <ContentsTimeline
          keys={model.keys}
          grain={data.grain}
          lineBy={model.lineBy}
          dotBy={model.dotBy}
          itemsBy={model.itemsBy}
          colorOf={model.colorOf}
          fmtBucket={fmtBucket}
          daysOf={(k) => daysInWindow(k, data.grain, data.from, data.to)}
          typeLabel={typeLabel}
          emptyNote={t('bpContents.lineEmpty')}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mt: 1 }}>
          <VizLegend
            items={[
              { label: t('bpContents.seriesCited'), color: VIZ.ink },
              ...model.legend.map((l) => ({ label: `${t('bpContents.seriesPublished')} · ${l.label}`, color: l.color })),
            ]}
          />
        </Box>
        {data.dots.undated > 0 && (
          <Typography sx={{ fontSize: 12, color: VIZ.muted, mt: 1 }}>
            {t('bpContents.undated', { count: data.dots.undated, n: fmtInt(data.dots.undated) })}
          </Typography>
        )}
      </ChartCard>

      <ChartCard title={t('bpContents.tableTitle')} dot={VIZ.serious} hint={t('bpContents.tableHint')}>
        {table.length === 0 ? (
          <EmptyNote>{t('bpContents.tableEmpty')}</EmptyNote>
        ) : (
          <>
            <VizTable
              columns={[
                t('bpTargets.colTargetContenu'),
                t('bpContents.colType'),
                t('bpContents.colPublishedAt'),
                t('bpTargets.colCitations'),
                t('bpContents.colLinked'),
              ]}
              align={['left', 'left', 'left', 'right', 'left']}
              rows={visible.map((r) => [
                contentCell(r),
                r.contentType ? typeLabel(r.contentType) : na('—'),
                r.publishedAt ? fmtDate(r.publishedAt) : na(t('bpContents.noDate')),
                fmtInt(r.c),
                linkedCell(r),
              ])}
            />
            {pageCount > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
                <Typography sx={{ fontSize: 12, color: VIZ.muted, mr: 1 }}>
                  {t('bpContents.pageOf', {
                    a: fmtInt(page * PAGE_SIZE + 1),
                    b: fmtInt(Math.min(table.length, (page + 1) * PAGE_SIZE)),
                    total: fmtInt(table.length),
                  })}
                </Typography>
                <IconButton size="small" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </ChartCard>

      <NoticePanel title={t('bpDash.caveatsTitle')} lines={caveatLines} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ContentsTimeline — the line (contents cited) over the publication dots
// ---------------------------------------------------------------------------

interface TimelineProps {
  keys: string[];
  grain: BPContentsGrain;
  lineBy: Map<string, BPContentsResponse['line'][number]>;
  dotBy: Map<string, BPContentsResponse['dots']['buckets'][number]>;
  itemsBy: Map<string, { color: string; title?: string }[]>;
  colorOf: (k: TypeKey) => string;
  fmtBucket: (key: string, long?: boolean) => string;
  daysOf: (key: string) => number;
  typeLabel: (k: TypeKey) => string;
  emptyNote: string;
}

const PLOT_H = 200;
const STRIP_H = 56;
const DOT_R = 3.5;
const DOT_STEP = 8;

function ContentsTimeline({
  keys,
  grain,
  lineBy,
  dotBy,
  itemsBy,
  colorOf,
  fmtBucket,
  daysOf,
  typeLabel,
  emptyNote,
}: TimelineProps) {
  const { t } = useTranslation();
  const [ref, width] = useMeasuredWidth<HTMLDivElement>(900);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tip = useVizTooltip();
  const [hover, setHover] = useState<number | null>(null);

  const np = keys.length;
  const hasLine = keys.some((k) => lineBy.has(k));
  const hasDots = keys.some((k) => (dotBy.get(k)?.total ?? 0) > 0 || itemsBy.has(k));
  if (!np || (!hasLine && !hasDots)) return <EmptyNote>{emptyNote}</EmptyNote>;

  const W = Math.max(360, width);
  const m = { t: 12, r: 16, l: 44, gap: 14, b: 26 };
  const H = m.t + PLOT_H + m.gap + STRIP_H + m.b;
  const pw = W - m.l - m.r;
  const band = pw / np;
  const X = (i: number) => m.l + (i + 0.5) * band;
  const max = Math.max(1, ...[...lineBy.values()].map((p) => p.contentsCited));
  const ticks = niceTicks(max, 2);
  const yTop = ticks[ticks.length - 1] || 1;
  const Y = (v: number) => m.t + PLOT_H - (v / yTop) * PLOT_H;
  const stripTop = m.t + PLOT_H + m.gap;
  const stripBase = stripTop + STRIP_H;

  // Null-breaking path: a missing bucket closes the subpath — never bridged, never 0.
  let d = '';
  let open = false;
  keys.forEach((k, i) => {
    const p = lineBy.get(k);
    if (!p) {
      open = false;
      return;
    }
    d += `${open ? 'L' : 'M'}${X(i).toFixed(1)},${Y(p.contentsCited).toFixed(1)}`;
    open = true;
  });

  const maxStack = Math.max(1, ...keys.map((k) => dotBy.get(k)?.total ?? 0));
  const capacity = Math.max(1, Math.floor(STRIP_H / DOT_STEP));

  const xTicks = [0, 1, 2, 3]
    .map((j) => Math.round((j / 3) * (np - 1)))
    .filter((v, i, a) => a.indexOf(v) === i);

  const linesAt = (i: number): string[] => {
    const k = keys[i];
    const p = lineBy.get(k);
    const cut = p?.partial || dotBy.get(k)?.partial;
    const out = [`${fmtBucket(k, true)}${cut ? ` · ${t('bpContents.partialShort')}` : ''}`];
    if (p) {
      out.push(`${t('bpContents.seriesCited')} : ${p.topTruncated ? '≥ ' : ''}${fmtInt(p.contentsCited)}`);
      out.push(`${t('bpTargets.colCitations')} : ${fmtInt(p.citations)}`);
      if (p.measuredDays != null) {
        out.push(`${t('bpContents.colMeasuredDays')} : ${fmtInt(p.measuredDays)} / ${fmtInt(daysOf(k))}`);
      }
    } else {
      out.push(`${t('bpContents.seriesCited')} : ${t('bpDash.notMeasuredShort')}`);
    }
    const items = itemsBy.get(k);
    const b = dotBy.get(k);
    const published = items?.length ?? b?.total ?? 0;
    if (published > 0) {
      out.push(`${t('bpContents.seriesPublished')} : ${fmtInt(published)}`);
      if (items) {
        for (const it of items.slice(0, 3)) if (it.title) out.push(`· ${it.title}`);
      } else if (b) {
        for (const [ty, n] of Object.entries(b.byContentType)) {
          if (n) out.push(`· ${typeLabel(ty as TypeKey)} : ${fmtInt(n)}`);
        }
      }
    }
    return out;
  };

  const onMove = (ev: React.PointerEvent<SVGRectElement>) => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return;
    const px = ((ev.clientX - box.left) / box.width) * W;
    const i = Math.max(0, Math.min(np - 1, Math.floor((px - m.l) / band)));
    setHover(i);
    tip.show(ev, linesAt(i));
  };

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: 'block', overflow: 'visible' }}
        role="img"
        aria-label={t('bpContents.title')}
      >
        {ticks.map((v) => (
          <g key={v}>
            <line x1={m.l} x2={m.l + pw} y1={Y(v)} y2={Y(v)} stroke={VIZ.grid} strokeWidth={1} />
            <text x={m.l - 8} y={Y(v) + 4} textAnchor="end" fill={VIZ.muted} fontSize={11} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmtInt(v)}
            </text>
          </g>
        ))}
        <line x1={m.l} x2={m.l + pw} y1={stripBase} y2={stripBase} stroke={VIZ.axis} strokeWidth={1} />
        <text x={m.l - 8} y={stripTop + STRIP_H / 2 + 4} textAnchor="end" fill={VIZ.muted} fontSize={10}>
          {t('bpContents.stripLabel')}
        </text>

        {xTicks.map((i, j) => (
          <text
            key={i}
            x={X(i)}
            y={H - 8}
            textAnchor={j === 0 && np > 1 ? 'start' : j === xTicks.length - 1 && np > 1 ? 'end' : 'middle'}
            fill={VIZ.muted}
            fontSize={11}
          >
            {fmtBucket(keys[i])}
          </text>
        ))}

        {hover != null && (
          <rect x={m.l + hover * band} y={m.t} width={band} height={stripBase - m.t} fill={VIZ.grid} opacity={0.6} />
        )}

        {/* publication dots / stacks */}
        {keys.map((k, i) => {
          const items = itemsBy.get(k);
          if (items && grain === 'day') {
            const shown = items.length > capacity ? capacity - 1 : items.length;
            return (
              <g key={`dots-${k}`}>
                {items.slice(0, shown).map((it, j) => (
                  <circle key={j} cx={X(i)} cy={stripBase - DOT_R - 1 - j * DOT_STEP} r={DOT_R} fill={it.color} />
                ))}
                {items.length > shown && (
                  <text x={X(i)} y={stripTop + 4} textAnchor="middle" fill={VIZ.muted} fontSize={9}>
                    +{items.length - shown}
                  </text>
                )}
              </g>
            );
          }
          const b = dotBy.get(k);
          if (!b || !b.total) return null;
          const bw = Math.max(3, Math.min(14, band * 0.6));
          let y = stripBase;
          // A cut edge bucket counts fewer days' publications: faded, so it doesn't read
          // as a publishing drop (same rule as the line's hollow marker).
          return (
            <g key={`stack-${k}`} opacity={b.partial ? 0.45 : 1}>
              {Object.entries(b.byContentType)
                .filter(([, n]) => (n ?? 0) > 0)
                .map(([ty, n]) => {
                  const h = ((n as number) / maxStack) * STRIP_H;
                  y -= h;
                  return <rect key={ty} x={X(i) - bw / 2} y={y} width={bw} height={h} fill={colorOf(ty as TypeKey)} />;
                })}
            </g>
          );
        })}

        <path d={d} fill="none" stroke={VIZ.ink} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {/* A marker on every measured bucket — an isolated week between two gaps would
            otherwise be invisible. Partial edge buckets are hollow + dashed. */}
        {keys.map((k, i) => {
          const p = lineBy.get(k);
          if (!p) return null;
          return p.partial ? (
            <circle key={`m-${k}`} cx={X(i)} cy={Y(p.contentsCited)} r={4} fill={VIZ.surface} stroke={VIZ.ink} strokeWidth={1.5} strokeDasharray="2 2" />
          ) : (
            <circle key={`m-${k}`} cx={X(i)} cy={Y(p.contentsCited)} r={3.5} fill={VIZ.ink} stroke={VIZ.surface} strokeWidth={1.5} />
          );
        })}

        <rect
          x={m.l}
          y={m.t}
          width={pw}
          height={stripBase - m.t}
          fill="transparent"
          style={{ cursor: 'crosshair' }}
          onPointerMove={onMove}
          onPointerLeave={() => {
            setHover(null);
            tip.hide();
          }}
        />
      </svg>
      {tip.node}
    </Box>
  );
}
